export type OnCancel = {
  readonly isCancelled: boolean;
  readonly isRejected: boolean;
  readonly isResolved: boolean;

  (cancelHandler: () => void): void;
};

export class CancelablePromise<T> implements Promise<T> {
  readonly _cancelHandlers: (() => void)[];
  _isCancelled: boolean;
  _isRejected: boolean;
  _isResolved: boolean;
  readonly _promise: Promise<T>;
  _reject?: (reason?: unknown) => void;
  _resolve?: (value: PromiseLike<T> | T) => void;

  readonly [Symbol.toStringTag] = "Cancellable Promise";

  public get isCancelled(): boolean {
    return this._isCancelled;
  }

  constructor(
    executor: (
      resolve: (value: PromiseLike<T> | T) => void,
      reject: (reason?: unknown) => void,
      onCancel: OnCancel,
    ) => void,
  ) {
    this._isResolved = false;
    this._isRejected = false;
    this._isCancelled = false;
    this._cancelHandlers = [];
    this._promise = new Promise<T>((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;

      const onResolve = (value: PromiseLike<T> | T): void => {
        if (this._isResolved || this._isRejected || this._isCancelled) {
          return;
        }
        this._isResolved = true;
        this._resolve?.(value);
      };

      const onReject = (reason?: unknown): void => {
        if (this._isResolved || this._isRejected || this._isCancelled) {
          return;
        }
        this._isRejected = true;
        this._reject?.(reason);
      };

      const onCancel = (cancelHandler: () => void): void => {
        if (this._isResolved || this._isRejected || this._isCancelled) {
          return;
        }
        this._cancelHandlers.push(cancelHandler);
      };

      Object.defineProperty(onCancel, "isResolved", {
        get: (): boolean => this._isResolved,
      });

      Object.defineProperty(onCancel, "isRejected", {
        get: (): boolean => this._isRejected,
      });

      Object.defineProperty(onCancel, "isCancelled", {
        get: (): boolean => this._isCancelled,
      });

      return executor(onResolve, onReject, onCancel as OnCancel);
    });
  }

  public cancel(): void {
    if (this._isResolved || this._isRejected || this._isCancelled) {
      return;
    }
    this._isCancelled = true;
    if (this._cancelHandlers.length) {
      try {
        for (const cancelHandler of this._cancelHandlers) {
          cancelHandler();
        }
      } catch (error) {
        console.warn("Cancellation threw an error", error);
        return;
      }
    }
    this._cancelHandlers.length = 0;
    this._reject?.(new CancelError("Request aborted"));
  }

  public async catch<TResult = never>(
    onRejected?: ((reason: unknown) => PromiseLike<TResult> | TResult) | null,
  ): Promise<T | TResult> {
    return await this._promise.catch(onRejected);
  }

  public async finally(onFinally?: (() => void) | null): Promise<T> {
    return await this._promise.finally(onFinally);
  }

  // biome-ignore lint/suspicious/noThenProperty: need this
  public async then<TResult1 = T, TResult2 = never>(
    onFulfilled?: ((value: T) => PromiseLike<TResult1> | TResult1) | null,
    onRejected?: ((reason: unknown) => PromiseLike<TResult2> | TResult2) | null,
  ): Promise<TResult1 | TResult2> {
    return await this._promise.then(onFulfilled, onRejected);
  }
}

export class CancelError extends Error {
  public readonly isCancelled = true;

  constructor(message: string) {
    super(message);
    this.name = "CancelError";
  }
}
